import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Icon, { ICONS, COLORS } from '../../../general-components/icon';
import { GreenroomState as GS } from '../../../../stores/types/types';
import TooltipWrapper from '../../../general-components/tooltips/tooltip-wrapper';
import { VirtualBg } from '../../../../stores/types/content';
import LoadingSpinner, { LoadingSpinnerType } from '../../../general-components/loading-spinner';
import * as l from './elements';
import { POP_TYPE } from './effects';
import VbgCardImg from './vbg-card-img';
import VbgCardBlur from './vbg-card-blur';
import ExpandFooter from './expand-footer';

interface OwnProps {
	onSelect: (background: VirtualBg) => void,
	onPopover: (e: React.MouseEvent, type: POP_TYPE, uniqueId?: string, url?: string) => void,
	onClear: () => void,
	isOnboarding?: boolean,
}

const LocalLoadingSpinner = () => (
	<LoadingSpinner height={24} width={24} stroke={10} type={LoadingSpinnerType.Gradient} />
);

export default function Sections(props: OwnProps) {
	const {isOnboarding = false} = props;
	const classname = isOnboarding ? 'onboarding' : '';

	const {
		defaultVBGS,
		uploadingVbgs,
		selectedVBG,
		recentVbgUpload,
	} = useSelector((s: GS) => s.ContentReducer);

	const myVirtualBackgrounds = useSelector(
		(s: GS) => s.ContentReducer.virtualBackgrounds ?? [],
	).reduce((curr: Array<VirtualBg>, bg: VirtualBg) => {
		curr.unshift(bg);
		return curr;
	}, []);

	const loading = useSelector((s: GS) => s.ContentReducer.loadingVbgs);

	const myBgsSection = useRef<HTMLDivElement>(null);
	const allBgsSection = useRef<HTMLDivElement>(null);

	const [sectionExpanded, setSectionExpanded] = useState({
		myBackgrounds: false,
		defaultBackgrounds: false,
	});

	const onExpandClick = (
		section: 'myBackgrounds' | 'defaultBackgrounds',
		elementCount: number, // number of elements in the section (+1 for the add card)
	) => {
		const element = section === 'myBackgrounds' ? myBgsSection.current : allBgsSection.current;
		const height = ((elementCount / 3) + 1) * (75 + 12);

		// set height to 3 rows or expanded height
		element?.style.setProperty('max-height', `${sectionExpanded[section] ? 261 : height}px`);
		setSectionExpanded((prev) => ({ ...prev, [section]: !prev[section] }));
	};

	return (<>
		<l.Section className={classname}>
			<l.Title>
				<Icon name={ICONS.PERSON_BLUR} size={16} color={COLORS.TEXT_GRAY_300} />
				<h6 className='bl-font-header'>Blur</h6>
			</l.Title>
			<l.CardContainer>
				<VbgCardBlur
					blur='low'
					text='Less'
					onSelect={props.onSelect}
					cssBlur={0.8}
					active={selectedVBG?.uniqueId === 'blur_low'}
				/>
				<VbgCardBlur
					blur='high'
					text='More'
					onSelect={props.onSelect}
					cssBlur={5}
					active={selectedVBG?.uniqueId === 'blur_high'}
				/>
			</l.CardContainer>
		</l.Section>
		<l.Section className={classname}>
			<l.Title>
				<Icon name={ICONS.PERSON} size={16} color={COLORS.TEXT_GRAY_300} />
				<h6 className='bl-font-header'>My Backgrounds</h6>
			</l.Title>
			<div>
				<l.CardContainer
					ref={myBgsSection}
					className={`${sectionExpanded.myBackgrounds ? 'expanded' : ''}`}
				>
					{loading
						? <l.LoadingMyBackgrounds> <LocalLoadingSpinner /> </l.LoadingMyBackgrounds>
						: <l.Card className='add-card' onClick={(e) => props.onPopover(e, POP_TYPE.ADD)}>
							<span className='bl-font-l2 add-text margin-right-extra-small'>Add new</span>
							<Icon name={isOnboarding ? ICONS.TEMPLATE_ADD : ICONS.CARROT_DOWN} size={12} color={COLORS.HOT_PINK} />
						</l.Card>
					}
					{(uploadingVbgs.size === 0 && !recentVbgUpload)
						? null
						: <l.Card>
							<LocalLoadingSpinner />
						</l.Card>}
					{myVirtualBackgrounds.map((bg, i) => (
						<VbgCardImg
							bg={bg}
							key={`${i}_${bg?.blur}_${bg?.url}_${bg?.uniqueId}}`}
							onSelect={props.onSelect}
							onPopover={props.onPopover}
							type={POP_TYPE.UPLOADED}
							active={selectedVBG?.uniqueId === bg.uniqueId}
						/>
					))}
				</l.CardContainer>
			</div>
			<ExpandFooter
				isExpanded={sectionExpanded.myBackgrounds}
				elementCount={myVirtualBackgrounds.length + 1}
				onExpandClick={onExpandClick}
				section='myBackgrounds'
			/>
		</l.Section>
		<l.Section className={classname}>
			<l.Title>
				<Icon name={ICONS.MEDIA} size={16} color={COLORS.TEXT_GRAY_300} />
				<h6 className='bl-font-header'>Backgrounds</h6>
				<TooltipWrapper message={'Available to all users in the room'}>
					<Icon name={ICONS.INFO_CIRCLE} size={12} color={COLORS.TEXT_GRAY_400} />
				</TooltipWrapper>
			</l.Title>
			<l.CardContainer
				ref={allBgsSection}
				className={`${sectionExpanded.defaultBackgrounds ? 'expanded' : ''}`}
			>
				{defaultVBGS.map((bg, i) => (
					<VbgCardImg
						bg={bg}
						key={`${i}_${bg?.blur}_${bg?.url}_${bg?.uniqueId}}`}
						onSelect={props.onSelect}
						onPopover={props.onPopover}
						type={POP_TYPE.DEFAULT}
						active={selectedVBG?.uniqueId === bg.uniqueId}
					/>
				))}
			</l.CardContainer>
			<ExpandFooter
				isExpanded={sectionExpanded.defaultBackgrounds}
				elementCount={defaultVBGS.length}
				onExpandClick={onExpandClick}
				section='defaultBackgrounds'
			/>
		</l.Section>
	</>);
}
